import React, { useEffect, useRef, useState } from 'react';
import { places } from './places';

export default function MapCanvas({
  assetBase,
  selected,
  focusRequest,
  category,
  hunt,
  found,
  onSelect,
  onFind,
  onStatus,
}) {
  const canvas = useRef(null);
  const controller = useRef(null);
  const buttons = useRef({});
  const lines = useRef({});
  const north = useRef(null);
  const state = useRef({ selected, category, hunt, found, onStatus });
  state.current = { selected, category, hunt, found, onStatus };

  const [status, setStatus] = useState('loading');
  const [progress, setProgress] = useState(0);
  const [retry, setRetry] = useState(0);
  const [view, setView] = useState('3d');
  const [orbit, setOrbit] = useState(false);
  const [reduced, setReduced] = useState(false);
  const ready = status === 'ready';

  const onFrame = useRef((points, bearing) => {
    if (north.current) north.current.style.transform = `rotate(${bearing}deg)`;
    const s = state.current;
    const placed = [];
    const sorted = [...points].sort(
      (a, b) => Number(b.id === s.selected) - Number(a.id === s.selected)
    );
    for (const p of sorted) {
      const button = buttons.current[p.id];
      const line = lines.current[p.id];
      const place = places.find((q) => q.id === p.id);
      if (!button || !line || !place) continue;
      let visible = p.visible && (place.category === s.category || p.id === s.selected);
      let x = p.x;
      let y = p.y;
      if (visible) {
        const offsets = [
          [0, 0],
          [0, -48],
          [48, 0],
          [-48, 0],
          [0, 48],
          [48, -48],
          [-48, -48],
        ];
        const w = canvas.current?.clientWidth || 0;
        const h = canvas.current?.clientHeight || 0;
        const candidate = offsets.find(([dx, dy]) => {
          const nx = p.x + dx;
          const ny = p.y + dy;
          return (
            nx > 24 &&
            ny > 70 &&
            nx < w - 24 &&
            ny < h - 70 &&
            !placed.some((q) => Math.abs(q.x - nx) < 44 && Math.abs(q.y - ny) < 44)
          );
        });
        if (candidate) {
          x += candidate[0];
          y += candidate[1];
          placed.push({ x, y });
        } else {
          visible = false;
        }
      }
      button.style.visibility = visible ? 'visible' : 'hidden';
      button.style.left = `${x}px`;
      button.style.top = `${y}px`;
      line.style.visibility = visible ? 'visible' : 'hidden';
      line.setAttribute('x1', String(p.x));
      line.setAttribute('y1', String(p.y));
      line.setAttribute('x2', String(x));
      line.setAttribute('y2', String(y));
    }
  });

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const change = () => setReduced(media.matches);
    change();
    media.addEventListener('change', change);
    return () => media.removeEventListener('change', change);
  }, []);

  useEffect(() => {
    const abort = new AbortController();
    let stopped = false;
    setStatus('loading');
    setProgress(0);
    onStatus('Loading the 3D landscape');

    import('./renderer').then(({ createRenderer }) => {
      if (stopped || !canvas.current) return;
      try {
        controller.current = createRenderer({
          canvas: canvas.current,
          assetBase,
          signal: abort.signal,
          onFrame: (p, b) => onFrame.current(p, b),
          onInteraction: () => {
            setOrbit(false);
            setView('3d');
          },
          onReady: () => {
            if (!stopped) {
              setStatus('ready');
              state.current.onStatus('3D map ready');
              controller.current?.setActive(state.current.selected);
            }
          },
          onProgress: (p) => {
            if (!stopped) setProgress(p);
          },
          onError: () => {
            if (!stopped) {
              setStatus('error');
              state.current.onStatus('3D view unavailable. All places are available in the list.');
            }
          },
        });
      } catch {
        setStatus('error');
        state.current.onStatus('3D view unavailable. All places are available in the list.');
      }
    }).catch(() => {
      if (!stopped) setStatus('error');
    });

    return () => {
      stopped = true;
      abort.abort();
      controller.current?.destroy();
      controller.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetBase, retry]);

  useEffect(() => {
    controller.current?.setActive(selected);
    if (focusRequest > 0) {
      controller.current?.orbit(false);
      setOrbit(false);
      controller.current?.focus(selected);
      setView('3d');
    }
  }, [selected, focusRequest]);

  useEffect(() => {
    controller.current?.setActive(selected);
  }, [category, hunt, found, selected]);

  function key(e) {
    if (!ready) return;
    const c = controller.current;
    if (!c) return;
    let used = true;
    switch (e.key) {
      case 'ArrowLeft':
        c.rotate(-0.15, 0);
        break;
      case 'ArrowRight':
        c.rotate(0.15, 0);
        break;
      case 'ArrowUp':
        c.rotate(0, -0.12);
        break;
      case 'ArrowDown':
        c.rotate(0, 0.12);
        break;
      case '+':
      case '=':
        c.zoom(1.2);
        break;
      case '-':
        c.zoom(1 / 1.2);
        break;
      case 'Home':
        c.home();
        setView('3d');
        break;
      default:
        used = false;
    }
    if (used) e.preventDefault();
  }

  return (
    <>
      {!ready && (
        <img
          className="be-overview"
          src={`${assetBase}/explore/belvoir-overview.jpg`}
          alt="Overview of Belvoir Castle and its surrounding garden paths"
        />
      )}
      <canvas
        ref={canvas}
        className={`be-canvas${ready ? ' is-ready' : ''}`}
        tabIndex={ready ? 0 : -1}
        aria-label="Interactive garden map. Arrow keys rotate and tilt, plus and minus zoom, Home resets."
        onKeyDown={key}
      />
      {ready && (
        <>
          <svg className="be-marker-lines" aria-hidden="true">
            {places.map((p) => (
              <line
                key={p.id}
                ref={(el) => {
                  lines.current[p.id] = el;
                }}
              />
            ))}
          </svg>
          <div className="be-markers">
            {places.map((p) => (
              <button
                type="button"
                key={p.id}
                ref={(el) => {
                  buttons.current[p.id] = el;
                }}
                className={`be-marker${p.id === selected ? ' is-active' : ''}${
                  hunt && p.bee ? ' is-bee' : ''
                }`}
                aria-label={
                  hunt && p.bee
                    ? `${found.includes(p.id) ? 'Bee found at ' : 'Find the bee at '}${p.name}`
                    : p.name
                }
                aria-pressed={p.id === selected}
                onClick={() => {
                  onSelect(p.id);
                  if (hunt && p.bee) onFind(p.id);
                }}
              >
                {hunt && p.bee ? (found.includes(p.id) ? '✓' : '🐝') : p.number}
                <span className="be-marker-name">{p.name}</span>
              </button>
            ))}
          </div>
        </>
      )}
      <div className="be-view-controls" aria-label="Map view">
        <button
          type="button"
          className={view === '3d' ? 'is-on' : ''}
          disabled={!ready}
          onClick={() => {
            controller.current?.home();
            setView('3d');
          }}
        >
          3D
        </button>
        <button
          type="button"
          className={view === 'top' ? 'is-on' : ''}
          disabled={!ready}
          onClick={() => {
            controller.current?.top();
            setView('top');
          }}
        >
          Top view
        </button>
        <button
          type="button"
          className={orbit ? 'is-on' : ''}
          disabled={!ready || reduced}
          aria-pressed={orbit}
          onClick={() => setOrbit(controller.current?.orbit(!orbit) || false)}
        >
          {orbit ? 'Pause' : 'Orbit'}
        </button>
      </div>
      {ready && (
        <div className="be-compass" aria-label="North">
          <span ref={north}>↑</span>
          <b>N</b>
        </div>
      )}
      <div className="be-zoom" aria-label="Map controls">
        <button type="button" aria-label="Zoom in" disabled={!ready} onClick={() => controller.current?.zoom(1.25)}>
          +
        </button>
        <button type="button" aria-label="Zoom out" disabled={!ready} onClick={() => controller.current?.zoom(0.8)}>
          −
        </button>
        <button
          type="button"
          aria-label="Reset map view"
          disabled={!ready}
          onClick={() => {
            controller.current?.home();
            setView('3d');
          }}
        >
          ↺
        </button>
      </div>
      {status === 'loading' && (
        <div className="be-load" role="status">
          <span className="be-spinner" />
          <span>Opening the gardens{progress > 0 ? ` · ${progress}%` : ''}</span>
        </div>
      )}
      {status === 'error' && (
        <div className="be-load be-error" role="status">
          <strong>The 3D view couldn’t open.</strong>
          <p>You can still explore every place in the list.</p>
          <button type="button" onClick={() => setRetry((n) => n + 1)}>
            Try 3D again
          </button>
        </div>
      )}
      {ready && (
        <div className="be-gesture">
          Drag to rotate <span>·</span> Pinch or scroll to zoom
        </div>
      )}
    </>
  );
}
